// SPDX-License-Identifier: GPL-3.0-or-later

import { getExtensionState } from '../ext/detect';
import { EMPTY_PROFILE, readProfile, writeProfile, type Profile } from '../fs/profile';
import { clearRoot } from '../fs/root';
import { isJourneyDismissed } from '../onboarding/journey';
import {
  listCustomTemplates,
  readTemplateMeta,
  writeDefaultTemplates,
  type TemplateMeta,
} from '../fs/templates';
import {
  analyzeAllTemplates,
  analyzeTemplate,
} from '../fs/template-adjust';
import {
  isSupportedCvFile,
  parseCvFile,
  SUPPORTED_CV_ACCEPT,
  type CvParseResult,
} from '../fs/cv-parse';
import {
  cleanProfileFromCv,
  persistSynthesisedCvTemplate,
  synthesiseCvTemplateFromCv,
} from '../ai/cv-import';
import type { DocKind } from '../generate';
import {
  downloadBlob,
  exportRootAsZip,
  importZipIntoRoot,
} from '../fs/zip';
import { IN_DEPTH_DEFAULT, QUICK_DEFAULT } from '../ai/client';
import { getManifest, loadManifest, updateConfig } from '../store/applications';
import { navigate } from '../router';
import { type ManifestConfig, type ModelChoice } from '../types';
import {
  connectWallet,
  disconnectWallet,
  getWalletState,
  subscribeWallet,
  WALLET_NOT_DETECTED,
} from '../wallet';
import { EXTENSION_STORE_URL } from './Onboarding';
import { el } from './dom';

function patternsToText(p: readonly string[]): string {
  return p.join(', ');
}

function textToPatterns(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface SettingsOptions {
  root: FileSystemDirectoryHandle;
  onReselect(): void;
  focusSection?: string | undefined;
}

interface Section {
  id: string;
  label: string;
  render(): HTMLElement;
}

export function renderSettings(opts: SettingsOptions): HTMLElement {
  const sections: Section[] = [
    { id: 'profile', label: 'Profile', render: () => renderProfileSection(opts.root) },
    { id: 'wallet', label: 'AI', render: renderWalletSection },
    { id: 'workspace', label: 'Workspace', render: () => renderWorkspaceSection(opts) },
    { id: 'templates', label: 'Templates', render: () => renderTemplatesSection(opts.root) },
    { id: 'detection', label: 'File detection', render: renderDetectionSection },
    { id: 'generation', label: 'Generation', render: renderGenerationSection },
    { id: 'backup', label: 'Backup', render: () => renderBackupSection(opts.root) },
  ];

  const nav = el(
    'aside',
    { class: 'settings-nav', 'aria-label': 'Settings sections' },
    ...sections.map((s) =>
      el(
        'a',
        {
          href: `#section-${s.id}`,
          onclick: (e: Event) => {
            e.preventDefault();
            const target = document.getElementById(`section-${s.id}`);
            target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            nav.querySelectorAll('a').forEach((a) => a.classList.remove('active'));
            (e.currentTarget as HTMLElement).classList.add('active');
          },
        },
        s.label,
      ),
    ),
  );

  const sectionsCol = el(
    'div',
    { class: 'settings-sections' },
    ...sections.map((s) =>
      el(
        'section',
        { id: `section-${s.id}`, class: 'card padded-lg' },
        s.render(),
      ),
    ),
  );

  const root = el(
    'div',
    { class: 'stack stack-lg' },
    el(
      'div',
      { class: 'stack stack-sm' },
      el('div', { class: 'eyebrow' }, 'Settings'),
      el('h1', { class: 'display-2' }, 'Configure your Applify.'),
      el(
        'p',
        { class: 'text-secondary', style: 'font-size: 0.85rem;' },
        'Every setting is stored on your device — either in the workspace folder or in browser storage.',
      ),
    ),
    el('div', { class: 'settings-shell' }, nav, sectionsCol),
  );

  if (opts.focusSection) {
    const id = `section-${opts.focusSection}`;
    requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const navLink = nav.querySelector(`a[href="#${id}"]`);
      if (navLink) {
        nav.querySelectorAll('a').forEach((a) => a.classList.remove('active'));
        navLink.classList.add('active');
      }
    });
  }

  return root;
}

/* ---------- Helpers ---------- */

function sectionHeader(title: string, subtitle?: string): HTMLElement {
  return el(
    'div',
    { class: 'card-header' },
    el(
      'div',
      {},
      el('h2', { class: 'card-title' }, title),
      subtitle ? el('div', { class: 'card-subtitle', style: 'margin-top: 0.3rem;' }, subtitle) : null,
    ),
  );
}

function field(labelText: string): HTMLElement {
  return el('div', { class: 'field-label' }, labelText);
}

function hint(...children: (Node | string)[]): HTMLElement {
  return el('div', { class: 'hint' }, ...children);
}

/* ---------- Sections ---------- */

function renderProfileSection(root: FileSystemDirectoryHandle): HTMLElement {
  const cvArea = el('textarea', {
    placeholder:
      'Paste your CV text or anything the AI should know about you (employers, roles, achievements, education, skills). Stored at <root>/.applify/profile.json — never uploaded.',
    class: 'settings-textarea',
    style: 'min-height: 14rem;',
  });

  const prefsArea = el('textarea', {
    placeholder:
      'Describe the roles you want. Industries, seniority, location, IC vs management, must-haves, dealbreakers. Leave empty to disable auto-screening.',
    class: 'settings-textarea',
    style: 'min-height: 8rem;',
  });

  // Past cover letters, used as voice/framing examples by the AI. Each entry
  // is a complete letter. Rendered as a dynamic list of textareas so the user
  // can add/remove samples without dealing with separator syntax.
  const coversList = el('div', { class: 'stack stack-sm' });
  const coverValues: string[] = [];

  const renderCovers = (): void => {
    const items = coverValues.map((value, idx) => {
      const area = el('textarea', {
        placeholder:
          'Paste one past cover letter. The AI will imitate its voice, framing, and recurring themes when drafting new ones.',
        class: 'settings-textarea',
        style: 'min-height: 10rem;',
      }) as HTMLTextAreaElement;
      area.value = value;
      area.addEventListener('input', () => {
        coverValues[idx] = area.value;
      });
      const removeBtn = el(
        'button',
        {
          class: 'btn ghost compact',
          type: 'button',
          onclick: () => {
            coverValues.splice(idx, 1);
            renderCovers();
          },
        },
        'Remove',
      );
      return el(
        'div',
        { class: 'stack stack-xs' },
        el(
          'div',
          { class: 'actions-row', style: 'justify-content: space-between; align-items: center;' },
          el('div', { class: 'text-faint', style: 'font-size: 0.78rem;' }, `Example ${idx + 1}`),
          removeBtn,
        ),
        area,
      );
    });
    if (items.length === 0) {
      items.push(
        el(
          'div',
          { class: 'text-faint', style: 'font-size: 0.82rem;' },
          'No examples yet. Add a past cover letter and Applify will match its voice when drafting new ones.',
        ),
      );
    }
    coversList.replaceChildren(...items);
  };

  const addCoverBtn = el(
    'button',
    {
      class: 'btn compact',
      type: 'button',
      onclick: () => {
        coverValues.push('');
        renderCovers();
      },
    },
    '+ Add a past cover letter',
  );

  const status = el('div', { class: 'status-text' });

  // After saving during onboarding, point the user at the next step — the wizard
  // itself lives on the dashboard, so without this the Settings page is a
  // dead-end ("profile set, now what?").
  const nextStep = el('div', { style: 'margin-top: 0.75rem;' });
  const showNextStep = (): void => {
    if (isJourneyDismissed()) {
      nextStep.replaceChildren();
      return;
    }
    if (!getExtensionState().installed) {
      nextStep.replaceChildren(
        el(
          'div',
          { class: 'inline-banner info' },
          el(
            'div',
            {},
            el('strong', {}, 'Profile saved! '),
            'Next, install the browser extension so you can save jobs with one click while you browse.',
          ),
          el(
            'div',
            { class: 'actions-row', style: 'margin-top: 0.6rem;' },
            el(
              'a',
              { class: 'btn primary compact', href: EXTENSION_STORE_URL, target: '_blank', rel: 'noreferrer' },
              'Install the extension →',
            ),
            el('a', { class: 'btn compact', href: '#/' }, 'Back to dashboard'),
          ),
        ),
      );
    } else {
      nextStep.replaceChildren(
        el(
          'div',
          { class: 'inline-banner info' },
          el(
            'div',
            {},
            el('strong', {}, 'Profile saved! '),
            "The extension is installed — head back to your dashboard to save your first job.",
          ),
          el(
            'div',
            { class: 'actions-row', style: 'margin-top: 0.6rem;' },
            el('a', { class: 'btn primary compact', href: '#/' }, 'Back to dashboard →'),
          ),
        ),
      );
    }
  };

  // Tracks what was on disk last time we loaded. Used to skip the template
  // re-analysis when the user saved without touching the rawText (changed
  // only preferences or cover examples).
  let lastLoadedRawText = '';

  const refresh = async (): Promise<void> => {
    const p = (await readProfile(root)) ?? EMPTY_PROFILE;
    cvArea.value = p.rawText ?? '';
    prefsArea.value = p.preferences ?? '';
    coverValues.splice(0, coverValues.length, ...(p.previousCovers ?? []));
    renderCovers();
    lastLoadedRawText = p.rawText ?? '';
    const lastSaved = p.updatedAt === EMPTY_PROFILE.updatedAt
      ? 'never'
      : new Date(p.updatedAt).toLocaleString();
    status.textContent = `Last saved: ${lastSaved}`;
  };
  void refresh();

  const save = async (): Promise<void> => {
    try {
      const cleanedCovers = coverValues
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const rawTextChanged = cvArea.value !== lastLoadedRawText;
      const next: Profile = {
        schemaVersion: 1,
        rawText: cvArea.value,
        preferences: prefsArea.value,
        previousCovers: cleanedCovers,
        updatedAt: new Date().toISOString(),
      };
      await writeProfile(root, next);
      status.textContent = 'Saved.';
      showNextStep();
      window.setTimeout(() => void refresh(), 1500);
      // Only re-analyze when the source of truth for placeholders (rawText)
      // actually moved. Preferences and past-cover examples don't change which
      // placeholders the profile can fill.
      if (rawTextChanged && getWalletState().connection) {
        void analyzeAllTemplates(root, {
          profileText: next.rawText ?? '',
          profileUpdatedAt: next.updatedAt,
        }).then(() => {
          window.dispatchEvent(new CustomEvent('applify:templates-analyzed'));
        });
      }
    } catch (err) {
      status.textContent = `Save failed: ${err instanceof Error ? err.message : String(err)}`;
    }
  };

  const uploadBlock = renderCvUploadBlock({
    root,
    getCurrentText: () => cvArea.value,
    setText: (next) => {
      cvArea.value = next;
      // Let downstream listeners (autosizers etc.) know.
      cvArea.dispatchEvent(new Event('input', { bubbles: true }));
    },
  });

  return el(
    'div',
    { class: 'stack stack-md' },
    sectionHeader(
      'Profile',
      'The source of truth for AI-generated CVs and cover letters. Stays on your machine.',
    ),
    uploadBlock,
    el(
      'div',
      { class: 'stack stack-xs' },
      field('About you (CV text, achievements, skills)'),
      cvArea,
      hint('Stored at ', el('code', { class: 'code-tag' }, '<root>/.applify/profile.json'), '. Never uploaded.'),
    ),
    el(
      'div',
      { class: 'stack stack-xs' },
      field('Job preferences'),
      prefsArea,
      hint(
        'When set and AI is on, jobs you save are auto-screened on capture. Mismatches land in the Filtered status with a short reason.',
      ),
    ),
    el(
      'div',
      { class: 'stack stack-sm' },
      field('Past cover letters (voice examples)'),
      coversList,
      el(
        'div',
        { class: 'actions-row' },
        addCoverBtn,
      ),
      hint(
        'When the AI drafts a cover letter, it will imitate the voice, framing, and recurring themes from these examples and draw on their content where it fits the role. Add as many as you like.',
      ),
    ),
    el(
      'div',
      { class: 'actions-row' },
      el('button', { class: 'btn primary', onclick: save }, 'Save profile'),
      status,
    ),
    nextStep,
  );
}

interface CvUploadOptions {
  root: FileSystemDirectoryHandle;
  getCurrentText(): string;
  setText(next: string): void;
}

function wordCount(s: string): number {
  const trimmed = s.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

function scrollToAiSection(): void {
  const target = document.getElementById('section-wallet');
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // Mirror what the in-page nav does so the side menu reflects the jump.
  const nav = document.querySelector('.settings-nav');
  if (nav) {
    nav.querySelectorAll('a').forEach((a) => a.classList.remove('active'));
    const navLink = nav.querySelector('a[href="#section-wallet"]');
    if (navLink) navLink.classList.add('active');
  }
}

function renderCvUploadBlock(opts: CvUploadOptions): HTMLElement {
  const fileInput = el('input', {
    type: 'file',
    accept: SUPPORTED_CV_ACCEPT,
    style: 'display: none;',
  }) as HTMLInputElement;

  const pickBtn = el(
    'button',
    {
      class: 'btn compact',
      type: 'button',
      onclick: () => fileInput.click(),
    },
    '📄 Upload existing CV',
  );

  const status = el('div', { class: 'status-text', style: 'min-height: 1.1em;' });
  const panel = el('div', { class: 'stack stack-sm', style: 'display: none;' });

  // Latest parse so the Apply handler doesn't re-parse on click. Cleared
  // when the user cancels or after a successful apply. Kept alongside the
  // bound file so we can re-render the panel when wallet state flips and the
  // gating banner needs to disappear / appear.
  let parsed: CvParseResult | null = null;
  let parsedFile: File | null = null;

  const resetPanel = (): void => {
    parsed = null;
    parsedFile = null;
    panel.style.display = 'none';
    panel.replaceChildren();
    fileInput.value = '';
  };

  const renderPanel = (file: File, p: CvParseResult): void => {
    parsed = p;
    parsedFile = file;

    const aiOn = !!getWalletState().connection;

    const modeAppend = el('input', {
      type: 'radio',
      name: 'cv-import-mode',
      value: 'append',
      checked: opts.getCurrentText().trim().length > 0 ? true : undefined,
    }) as HTMLInputElement;
    const modeReplace = el('input', {
      type: 'radio',
      name: 'cv-import-mode',
      value: 'replace',
      checked: opts.getCurrentText().trim().length === 0 ? true : undefined,
    }) as HTMLInputElement;

    const tplCheckbox = el('input', {
      type: 'checkbox',
      disabled: aiOn ? undefined : true,
    }) as HTMLInputElement;

    const previewPre = el(
      'pre',
      {
        style:
          'max-height: 9rem; overflow: auto; background: var(--bg-soft, rgba(0,0,0,0.04)); padding: 0.5rem 0.6rem; border-radius: 6px; font-size: 0.72rem; white-space: pre-wrap; margin: 0;',
      },
      p.text.length > 1200 ? `${p.text.slice(0, 1200)}…` : p.text || '(no text extracted)',
    );

    const pageNote = p.kind === 'pdf' && p.pageCount
      ? ` · ${p.pageCount} page${p.pageCount === 1 ? '' : 's'}`
      : '';

    const apply = async (): Promise<void> => {
      if (!parsed) return;
      if (!getWalletState().connection) {
        // Shouldn't happen — the banner replaces the action row when AI is
        // off — but guard anyway in case the wallet disconnected mid-click.
        status.textContent = 'AI is off — set it up under the AI section first.';
        return;
      }
      applyBtn.setAttribute('disabled', 'true');
      cancelBtn.setAttribute('disabled', 'true');
      status.textContent = 'Asking AI to clean the extracted text…';
      try {
        const cleaned = await cleanProfileFromCv(parsed);
        const cleanedText = cleaned.text.trim();
        const existing = opts.getCurrentText();
        if (modeReplace.checked) {
          opts.setText(cleanedText);
        } else {
          const sep = existing.trim() ? `${existing.trim()}\n\n---\n\n` : '';
          opts.setText(`${sep}${cleanedText}`);
        }
        status.textContent = tplCheckbox.checked
          ? 'Cleaned text appended. Synthesising Typst template from the file…'
          : 'Cleaned text appended. Click "Save profile" below to persist.';

        if (tplCheckbox.checked) {
          try {
            const synth = await synthesiseCvTemplateFromCv(parsed);
            await persistSynthesisedCvTemplate(opts.root, synth);
            status.textContent =
              'Cleaned text appended and CV template synthesised. Click "Save profile" below to persist the profile.';
          } catch (err) {
            status.textContent = `Cleaned text appended, but template synthesis failed: ${err instanceof Error ? err.message : String(err)}`;
          }
        }
        resetPanel();
      } catch (err) {
        status.textContent = `Import failed: ${err instanceof Error ? err.message : String(err)}`;
        applyBtn.removeAttribute('disabled');
        cancelBtn.removeAttribute('disabled');
      }
    };

    const applyBtn = el(
      'button',
      {
        class: 'btn primary compact',
        type: 'button',
        onclick: () => void apply(),
      },
      'Process with AI',
    );
    const cancelBtn = el(
      'button',
      { class: 'btn ghost compact', type: 'button', onclick: resetPanel },
      'Cancel',
    );

    // Two flavours of the bottom action row: when AI is on, the user gets
    // Process + Cancel. When AI is off, an inline banner explains what's
    // missing and offers a one-click jump to the AI section (which lives
    // further down this same Settings page).
    const actionArea = aiOn
      ? el(
        'div',
        { class: 'stack stack-xs' },
        el(
          'div',
          { class: 'text-faint', style: 'font-size: 0.75rem;' },
          'Sends the extracted text to your AI to rewrite it as clean profile sections (Contact, Experience, Skills…).',
        ),
        el(
          'div',
          { class: 'actions-row' },
          applyBtn,
          cancelBtn,
        ),
      )
      : el(
        'div',
        { class: 'inline-banner warn' },
        el(
          'div',
          {},
          el('strong', {}, 'AI setup required. '),
          'Processing reads the extracted text with your AI to rewrite it as a clean profile (and, optionally, a matching Typst template). Set up the Injinary Wallet under the AI section, then come back here.',
        ),
        el(
          'div',
          { class: 'actions-row', style: 'margin-top: 0.6rem;' },
          el(
            'button',
            {
              class: 'btn primary compact',
              type: 'button',
              onclick: () => scrollToAiSection(),
            },
            'Set up AI →',
          ),
          el(
            'button',
            { class: 'btn ghost compact', type: 'button', onclick: resetPanel },
            'Cancel',
          ),
        ),
      );

    panel.replaceChildren(
      el(
        'div',
        { class: 'inline-banner info' },
        el(
          'div',
          {},
          el('strong', {}, file.name),
          el('span', { class: 'text-faint', style: 'margin-left: 0.5rem; font-size: 0.78rem;' }, `${p.kind.toUpperCase()} · ${wordCount(p.text)} words${pageNote}`),
        ),
      ),
      el(
        'div',
        { class: 'stack stack-xs' },
        el('div', { class: 'text-faint', style: 'font-size: 0.75rem;' }, 'Extracted text (preview)'),
        previewPre,
      ),
      el(
        'div',
        { class: 'stack stack-xs' },
        el('label', { style: 'display: flex; align-items: center; gap: 0.45rem; font-size: 0.85rem;' }, modeAppend, 'Append to existing profile'),
        el('label', { style: 'display: flex; align-items: center; gap: 0.45rem; font-size: 0.85rem;' }, modeReplace, 'Replace existing profile'),
      ),
      el(
        'label',
        {
          style: `display: flex; align-items: center; gap: 0.45rem; font-size: 0.85rem;${aiOn ? '' : ' opacity: 0.55;'}`,
        },
        tplCheckbox,
        el('span', {}, 'Also generate a Typst CV template matching this file\'s layout', aiOn ? '' : ' (AI required)'),
      ),
      actionArea,
    );
    panel.style.display = '';
  };

  // If the user jumps to the AI section, configures the wallet, and comes
  // back, re-render the panel so the gating banner is replaced with the
  // real action row. Cheap: the parse result is reused, no re-upload.
  //
  // subscribeWallet fires the callback synchronously on subscribe, so we
  // declare the unsubscribe handle up-front to avoid a TDZ ReferenceError —
  // the first call lands before the `const` slot would have been initialised.
  let unsubscribeWallet: (() => void) | null = null;
  unsubscribeWallet = subscribeWallet(() => {
    if (parsed && parsedFile && panel.isConnected) {
      renderPanel(parsedFile, parsed);
    }
    if (!panel.isConnected) unsubscribeWallet?.();
  });

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    if (!isSupportedCvFile(file)) {
      status.textContent = `Unsupported file: ${file.name}. Use PDF, DOCX, TXT, MD or HTML.`;
      fileInput.value = '';
      return;
    }
    status.textContent = `Extracting text from ${file.name}…`;
    panel.style.display = 'none';
    void (async () => {
      try {
        const p = await parseCvFile(file);
        if (!p.text.trim()) {
          status.textContent = `No text extracted from ${file.name}. Is it a scanned image PDF? Try a text-based export.`;
          fileInput.value = '';
          return;
        }
        status.textContent = `Extracted ${wordCount(p.text)} words. Review and click "Process with AI" to clean it.`;
        renderPanel(file, p);
      } catch (err) {
        status.textContent = `Failed to read ${file.name}: ${err instanceof Error ? err.message : String(err)}`;
        fileInput.value = '';
      }
    })();
  });

  return el(
    'div',
    { class: 'stack stack-sm' },
    el(
      'div',
      { class: 'actions-row', style: 'align-items: center;' },
      pickBtn,
      fileInput,
      status,
    ),
    hint(
      'PDF, DOCX, TXT, MD or HTML. Applify extracts the text on your device, then asks the AI to clean it up before appending it to the textarea below. The file itself never leaves your machine.',
    ),
    panel,
  );
}

const WALLET_INSTALL_URL =
  'https://chromewebstore.google.com/detail/injinary-wallet/emnpfdhpjmgbdgmbpcbloncillceljgp';

// The wallet's model list may omit the provider on each entry; infer it from the
// model id so we don't drop otherwise-usable models from the dropdowns.
function inferProvider(id: string, accepted: string[]): string {
  const lid = id.toLowerCase();
  if (lid.includes('claude')) return 'anthropic';
  if (/^(gpt|o1|o3|o4|chatgpt|text-|davinci)/.test(lid)) return 'openai';
  if (lid.includes('gemini')) return 'google';
  if (lid.includes('mistral') || lid.includes('mixtral') || lid.includes('codestral')) return 'mistral';
  if (lid.includes('grok')) return 'xai';
  if (lid.includes('llama')) return 'meta';
  if (lid.includes('deepseek')) return 'deepseek';
  return accepted[0] ?? '';
}

function renderWalletSection(): HTMLElement {
  const container = el('div', { class: 'stack stack-md' });
  const statusWrap = el('div', { class: 'connection-status' });
  const actionsRow = el('div', { class: 'actions-row' });
  const errMsg = el(
    'div',
    { style: 'font-size: 0.75rem; color: var(--status-rejected); min-height: 1.1em;' },
  );
  // During onboarding, point the user back to the dashboard once AI is on.
  const nextStep = el('div', {});

  const quickSelect = el('select', { class: 'settings-input' }) as HTMLSelectElement;
  const inDepthSelect = el('select', { class: 'settings-input' }) as HTMLSelectElement;

  const modelLabel = (m: ModelChoice): string => `${m.provider} · ${m.id}`;
  const modelKey = (m: ModelChoice): string => `${m.provider}/${m.id}`;
  const parseKey = (k: string): ModelChoice => {
    const i = k.indexOf('/');
    return { provider: k.slice(0, i), id: k.slice(i + 1) };
  };
  const currentChoice = (role: 'quick' | 'inDepth'): ModelChoice => {
    const c = getManifest()?.config;
    if (role === 'quick') return c?.quickModel ?? QUICK_DEFAULT;
    return c?.inDepthModel ?? IN_DEPTH_DEFAULT;
  };
  const fillSelect = (
    select: HTMLSelectElement,
    models: ModelChoice[],
    current: ModelChoice,
  ): void => {
    const all = [...models];
    if (!all.some((m) => m.provider === current.provider && m.id === current.id)) {
      all.unshift(current);
    }
    const seen = new Set<string>();
    const opts: HTMLOptionElement[] = [];
    for (const m of all) {
      const k = modelKey(m);
      if (seen.has(k)) continue;
      seen.add(k);
      opts.push(el('option', { value: k }, modelLabel(m)) as HTMLOptionElement);
    }
    select.replaceChildren(...opts);
    select.value = modelKey(current);
  };

  fillSelect(quickSelect, [], currentChoice('quick'));
  fillSelect(inDepthSelect, [], currentChoice('inDepth'));

  quickSelect.addEventListener('change', () => {
    void updateConfig({ quickModel: parseKey(quickSelect.value) });
  });
  inDepthSelect.addEventListener('change', () => {
    void updateConfig({ inDepthModel: parseKey(inDepthSelect.value) });
  });

  let lastModelsKey = '';
  const loadModelList = async (): Promise<void> => {
    const conn = getWalletState().connection;
    if (!conn) return;
    const accepted = getWalletState().permissions?.providers ?? [];
    let infos: ModelChoice[] = [];
    try {
      const raw = await conn.listModels();
      infos = raw
        .filter((m) => typeof m.id === 'string' && m.id.length > 0)
        .map((m) => ({
          provider:
            typeof m.provider === 'string' && m.provider
              ? m.provider
              : inferProvider(m.id, accepted),
          id: m.id,
        }));
    } catch {
      modelsNote.textContent =
        "Couldn't load the wallet's model list — make sure the wallet is unlocked. Your selected model still works.";
      return;
    }
    if (infos.length === 0) {
      modelsNote.textContent =
        'The wallet returned no models for the connected providers. Add a provider key in the wallet, or keep the default.';
      return;
    }
    modelsNote.textContent = '';
    const key = infos.map(modelKey).sort().join(',');
    if (key === lastModelsKey) return;
    lastModelsKey = key;
    fillSelect(quickSelect, infos, currentChoice('quick'));
    fillSelect(inDepthSelect, infos, currentChoice('inDepth'));
  };

  const modelsNote = el('div', { class: 'hint', style: 'color: var(--text-muted);' });

  const modelsBox = el(
    'div',
    { class: 'stack stack-sm', style: 'display: none;' },
    el(
      'div',
      { class: 'stack stack-xs' },
      field('Quick model'),
      quickSelect,
      hint('Used to auto-screen new jobs against your preferences. A small, cheap model is enough.'),
    ),
    el(
      'div',
      { class: 'stack stack-xs' },
      field('In-depth model'),
      inDepthSelect,
      hint('Used to draft CVs, cover letters, and in-editor edits. Pick a stronger model — output quality lives here.'),
    ),
    modelsNote,
  );

  const paint = (): void => {
    const s = getWalletState();
    statusWrap.className = `connection-status${s.connection ? ' on' : ''}`;
    if (s.connection) {
      const providers = s.permissions?.providers?.join(', ') ?? 'connected';
      const budget = s.budget
        ? ` · ${s.budget.remaining}/${s.budget.limit} ${s.budget.period}`
        : '';
      statusWrap.replaceChildren(
        el('span', { class: 'pulse' }),
        el('span', {}, `Connected — ${providers}${budget}`),
      );
      actionsRow.replaceChildren(
        el('button', { class: 'btn', onclick: disconnectWallet, disabled: s.busy }, 'Disconnect'),
      );
      modelsBox.style.display = '';
      void loadModelList();
    } else {
      statusWrap.replaceChildren(
        el('span', { class: 'pulse' }),
        el('span', { class: 'text-secondary' }, 'AI is off — turn it on to draft CVs, cover letters, and in-editor edits.'),
      );
      actionsRow.replaceChildren(
        el(
          'button',
          {
            class: 'btn primary',
            onclick: () => void connectWallet(),
            disabled: s.busy,
          },
          s.busy ? 'Connecting…' : 'Use AI',
        ),
      );
      modelsBox.style.display = 'none';
      lastModelsKey = '';
    }
    if (s.connection && !isJourneyDismissed()) {
      const jobCount = getManifest()?.applications.length ?? 0;
      nextStep.replaceChildren(
        el(
          'div',
          { class: 'inline-banner info' },
          el(
            'div',
            {},
            el('strong', {}, 'AI is connected! 🎉 '),
            jobCount > 0
              ? 'Head back to your dashboard to screen your saved jobs against your profile and generate tailored documents.'
              : 'Head back to your dashboard — save a job and you can generate a tailored CV and cover letter.',
          ),
          el(
            'div',
            { class: 'actions-row', style: 'margin-top: 0.6rem;' },
            el('a', { class: 'btn primary compact', href: '#/' }, 'Back to dashboard →'),
          ),
        ),
      );
    } else {
      nextStep.replaceChildren();
    }
    if (s.lastError === WALLET_NOT_DETECTED) {
      errMsg.replaceChildren(
        el('div', {}, WALLET_NOT_DETECTED),
        el(
          'div',
          { style: 'margin-top: 0.45rem; display: flex; gap: 0.5rem; flex-wrap: wrap;' },
          el('button', { class: 'btn compact', onclick: () => location.reload() }, 'Reload page'),
          el(
            'a',
            { class: 'btn compact', href: WALLET_INSTALL_URL, target: '_blank', rel: 'noreferrer' },
            'Install the wallet →',
          ),
        ),
        el(
          'div',
          { style: 'margin-top: 0.4rem; color: var(--text-muted);' },
          'Already installed it? Reload this page so the wallet can connect. Still nothing? Make sure the wallet is enabled for this site.',
        ),
      );
    } else {
      errMsg.textContent = s.lastError ?? '';
    }
  };

  subscribeWallet(paint);

  container.append(
    sectionHeader(
      'AI',
      'Applify uses AI to draft tailored CVs and cover letters, suggest in-editor edits, and auto-screen new jobs against your preferences. Each call goes to the Injinary Wallet extension, which holds your API key and forwards the request to the provider you pick (Anthropic, OpenAI, …). You pay your provider per call — no Applify subscription — and you cap or revoke spend in the wallet at any time. Applify never sees your key or your spend.',
    ),
    statusWrap,
    actionsRow,
    errMsg,
    nextStep,
    modelsBox,
    hint(
      'Install the ',
      el(
        'a',
        {
          href: WALLET_INSTALL_URL,
          target: '_blank',
          rel: 'noreferrer',
        },
        'Injinary Wallet',
      ),
      ' extension once and add your API key (Anthropic, OpenAI, …) inside it. The key stays in your browser. Applify asks the wallet to run each AI call and only ever receives the model\'s response — never your key.',
    ),
  );
  return container;
}

function renderWorkspaceSection(opts: SettingsOptions): HTMLElement {
  const resetOnboarding = async (): Promise<void> => {
    if (
      !confirm(
        'Forget the saved folder? Your files on disk are untouched; you will pick the folder again on next launch.',
      )
    )
      return;
    await clearRoot();
    navigate('onboarding');
    location.reload();
  };

  return el(
    'div',
    { class: 'stack stack-md' },
    sectionHeader('Workspace', 'The folder Applify reads from and writes to.'),
    el(
      'div',
      { class: 'kv-row' },
      field('Root folder'),
      el(
        'div',
        { class: 'kv-value' },
        el('code', { class: 'code-tag' }, opts.root.name),
        el('span', { class: 'text-faint', style: 'margin-left: 0.6rem; font-size: 0.78rem;' }, 'picked once on first launch'),
      ),
    ),
    el(
      'div',
      { class: 'actions-row' },
      el('button', { class: 'btn', onclick: opts.onReselect }, 'Re-select folder'),
      el('button', { class: 'btn danger', onclick: resetOnboarding }, 'Forget folder'),
    ),
  );
}

const TEMPLATE_KINDS: { kind: DocKind; label: string }[] = [
  { kind: 'cv', label: 'CV' },
  { kind: 'cover', label: 'Cover letter' },
  { kind: 'interview', label: 'Interview prep' },
];

function renderTemplatesSection(root: FileSystemDirectoryHandle): HTMLElement {
  const status = el('div', { class: 'status-text' });
  const list = el('div', { class: 'stack stack-sm', style: 'font-size: 0.82rem;' });
  let busyKinds = new Set<DocKind>();

  const reanalyzeOne = async (kind: DocKind): Promise<void> => {
    if (!getWalletState().connection) {
      status.textContent = 'AI is off — turn it on under "AI" to analyze templates.';
      return;
    }
    const profile = await readProfile(root);
    if (!profile?.rawText || !profile.rawText.trim()) {
      status.textContent = 'No profile saved yet — paste your CV under "Profile" first.';
      return;
    }
    busyKinds.add(kind);
    status.textContent = `Analyzing ${kind}…`;
    void refresh();
    try {
      const out = await analyzeTemplate(root, kind, {
        profileText: profile.rawText,
        profileUpdatedAt: profile.updatedAt,
      });
      status.textContent = out.ok
        ? `Re-analyzed ${kind}.`
        : `Analyze ${kind} failed: ${out.message ?? 'unknown error'}`;
    } catch (err) {
      status.textContent = `Analyze ${kind} failed: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      busyKinds.delete(kind);
      void refresh();
    }
  };

  const reanalyzeAll = async (): Promise<void> => {
    if (!getWalletState().connection) {
      status.textContent = 'AI is off — turn it on under "AI" to analyze templates.';
      return;
    }
    const profile = await readProfile(root);
    if (!profile?.rawText || !profile.rawText.trim()) {
      status.textContent = 'No profile saved yet — paste your CV under "Profile" first.';
      return;
    }
    busyKinds = new Set(TEMPLATE_KINDS.map((t) => t.kind));
    status.textContent = 'Analyzing all templates…';
    void refresh();
    try {
      const results = await Promise.all(
        TEMPLATE_KINDS.map(({ kind }) =>
          analyzeTemplate(root, kind, {
            profileText: profile.rawText!,
            profileUpdatedAt: profile.updatedAt,
          }),
        ),
      );
      const failed = results.filter((r) => !r.ok);
      status.textContent = failed.length === 0
        ? 'Re-analyzed all three templates.'
        : `Re-analyzed with ${failed.length} failure${failed.length === 1 ? '' : 's'}: ${failed.map((f) => f.kind).join(', ')}.`;
    } catch (err) {
      status.textContent = `Analyze failed: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      busyKinds = new Set();
      void refresh();
    }
  };

  const refresh = async (): Promise<void> => {
    try {
      const presence = await listCustomTemplates(root);
      const metas = await Promise.all(
        TEMPLATE_KINDS.map(({ kind }) => readTemplateMeta(root, kind)),
      );
      list.replaceChildren(
        ...TEMPLATE_KINDS.map(({ kind, label }, i) =>
          kindRow(
            kind,
            label,
            presence[kind],
            metas[i] ?? null,
            busyKinds.has(kind),
            () => void reanalyzeOne(kind),
          ),
        ),
      );
    } catch (err) {
      list.textContent = `Could not list templates: ${err instanceof Error ? err.message : String(err)}`;
    }
  };
  void refresh();

  // The profile-save handler dispatches this after a background analysis run
  // so this section reflects the new gap list without a manual refresh. We
  // self-detach once the section's element is gone from the DOM — there's no
  // explicit teardown hook in this view layer.
  const onAnalyzed = (): void => {
    if (!list.isConnected) {
      window.removeEventListener('applify:templates-analyzed', onAnalyzed);
      return;
    }
    void refresh();
  };
  window.addEventListener('applify:templates-analyzed', onAnalyzed);

  const writeDefaults = async (): Promise<void> => {
    try {
      const { written } = await writeDefaultTemplates(root);
      status.textContent = written.length === 0
        ? 'All three custom templates already exist — nothing overwritten.'
        : `Wrote default ${written.join(', ')} template${written.length === 1 ? '' : 's'} to .applify/templates/.`;
      void refresh();
    } catch (err) {
      status.textContent = `Failed: ${err instanceof Error ? err.message : String(err)}`;
    }
  };

  return el(
    'div',
    { class: 'stack stack-md' },
    sectionHeader(
      'Templates',
      'Override the built-in Typst templates with your own. When your profile lacks information for some placeholders, Applify writes a trimmed version next to the original and uses that for generation.',
    ),
    list,
    el(
      'div',
      { class: 'actions-row' },
      el('button', { class: 'btn', onclick: () => void reanalyzeAll() }, 'Re-analyze all'),
      el('button', { class: 'btn ghost', onclick: writeDefaults }, 'Write defaults to disk'),
      el('button', { class: 'btn ghost', onclick: () => void refresh() }, 'Refresh'),
      status,
    ),
    hint(
      'Drop your own ',
      el('code', { class: 'code-tag' }, 'cv.typ'),
      ' / ',
      el('code', { class: 'code-tag' }, 'cover.typ'),
      ' / ',
      el('code', { class: 'code-tag' }, 'interview.typ'),
      ' under ',
      el('code', { class: 'code-tag' }, '<root>/.applify/templates/'),
      ' and Applify will use them instead of the built-ins. The adjusted version lives next to it as ',
      el('code', { class: 'code-tag' }, '<kind>.active.typ'),
      '. Templates are ',
      el(
        'a',
        { href: 'https://typst.app/docs/', target: '_blank', rel: 'noreferrer' },
        'Typst',
      ),
      ' source. ',
      el('code', { class: 'code-tag' }, '{{var}}'),
      ' is escaped, ',
      el('code', { class: 'code-tag' }, '{{{var}}}'),
      ' is raw, ',
      el('code', { class: 'code-tag' }, '{{#section}}…{{/section}}'),
      ' iterates arrays.',
    ),
  );
}

function kindRow(
  _kind: DocKind,
  label: string,
  overridden: boolean,
  meta: TemplateMeta | null,
  busy: boolean,
  onReanalyze: () => void,
): HTMLElement {
  const dot = el('span', {
    style: `display: inline-block; width: 0.6rem; height: 0.6rem; border-radius: 50%; background: ${overridden ? 'var(--accent)' : 'var(--border-strong)'}; flex-shrink: 0;`,
  });

  const headerLine = el(
    'div',
    { style: 'display: flex; align-items: center; gap: 0.55rem;' },
    dot,
    el('span', { class: 'text-secondary' }, label),
    el(
      'span',
      { class: 'text-faint' },
      overridden ? '— using your template' : '— using built-in',
    ),
    el('span', { style: 'flex: 1;' }),
    el(
      'button',
      {
        class: 'btn ghost compact',
        type: 'button',
        onclick: onReanalyze,
        disabled: busy,
      },
      busy ? 'Analyzing…' : 'Re-analyze',
    ),
  );

  const detailParts: (Node | string)[] = [];
  if (meta?.error) {
    detailParts.push(
      el(
        'div',
        { style: 'color: var(--status-rejected); margin-top: 0.25rem;' },
        `Last analysis failed: ${meta.error}`,
      ),
    );
  } else if (meta && meta.gaps.length > 0) {
    const sections = meta.gaps.filter((g) => g.scope === 'section');
    const fields = meta.gaps.filter((g) => g.scope === 'field');
    const parts: string[] = [];
    if (sections.length > 0) {
      parts.push(`${sections.length} section${sections.length === 1 ? '' : 's'} trimmed (${sections.map((g) => g.path).join(', ')})`);
    }
    if (fields.length > 0) {
      parts.push(`${fields.length} field${fields.length === 1 ? '' : 's'} will be empty (${fields.map((g) => g.path).join(', ')})`);
    }
    detailParts.push(
      el(
        'div',
        { class: 'text-faint', style: 'margin-top: 0.25rem;' },
        parts.join(' · '),
      ),
    );
    if (meta.notes) {
      detailParts.push(
        el(
          'div',
          { class: 'text-faint', style: 'margin-top: 0.15rem; font-style: italic;' },
          meta.notes,
        ),
      );
    }
  } else if (meta) {
    detailParts.push(
      el(
        'div',
        { class: 'text-faint', style: 'margin-top: 0.25rem;' },
        'Profile fills every placeholder — no trimming needed.',
      ),
    );
  }
  if (meta?.analyzedAt) {
    detailParts.push(
      el(
        'div',
        { class: 'text-faint', style: 'margin-top: 0.15rem; font-size: 0.75rem;' },
        `Last analyzed: ${new Date(meta.analyzedAt).toLocaleString()}`,
      ),
    );
  }

  return el(
    'div',
    { style: 'padding: 0.4rem 0;' },
    headerLine,
    ...detailParts,
  );
}

function renderDetectionSection(): HTMLElement {
  const manifest = getManifest();
  const config: ManifestConfig = manifest?.config ?? {
    cvPatterns: [],
    motivationPatterns: [],
    generateOnSave: 'manual',
  };

  const cvInput = el('input', {
    type: 'text',
    value: patternsToText(config.cvPatterns),
    placeholder: '*CV*, *Resume*',
    class: 'settings-input',
  });

  const motivationInput = el('input', {
    type: 'text',
    value: patternsToText(config.motivationPatterns),
    placeholder: '*Motivation*, *Cover*',
    class: 'settings-input',
  });

  const status = el('div', { class: 'status-text' });

  const save = async (): Promise<void> => {
    try {
      const cur = getManifest()?.config ?? config;
      await updateConfig({
        cvPatterns: textToPatterns(cvInput.value),
        motivationPatterns: textToPatterns(motivationInput.value),
        generateOnSave: cur.generateOnSave,
      });
      status.textContent = 'Saved.';
      window.setTimeout(() => (status.textContent = ''), 2000);
    } catch (err) {
      status.textContent = `Save failed: ${err instanceof Error ? err.message : String(err)}`;
    }
  };

  return el(
    'div',
    { class: 'stack stack-md' },
    sectionHeader(
      'File detection',
      'How Applify decides which files in a job folder are the CV and the cover letter.',
    ),
    el(
      'div',
      { class: 'stack stack-xs' },
      field('CV filename patterns'),
      cvInput,
      hint('Comma-separated. A file matches if its name contains any pattern (case-insensitive). Asterisks are ignored.'),
    ),
    el(
      'div',
      { class: 'stack stack-xs' },
      field('Motivation letter filename patterns'),
      motivationInput,
      hint('Same matching rules as CV patterns.'),
    ),
    el(
      'div',
      { class: 'actions-row' },
      el('button', { class: 'btn primary', onclick: save }, 'Save patterns'),
      status,
    ),
  );
}

function renderGenerationSection(): HTMLElement {
  const manifest = getManifest();
  const config: ManifestConfig = manifest?.config ?? {
    cvPatterns: [],
    motivationPatterns: [],
    generateOnSave: 'manual',
  };

  const generateSelect = el(
    'select',
    { class: 'settings-input' },
    el(
      'option',
      { value: 'manual', selected: config.generateOnSave === 'manual' },
      'Manual — wait for me to click Generate',
    ),
    el(
      'option',
      { value: 'auto', selected: config.generateOnSave === 'auto' },
      'Auto — generate CV + cover the moment a job lands',
    ),
  );

  const status = el('div', { class: 'status-text' });

  const save = async (): Promise<void> => {
    try {
      const cur = getManifest()?.config ?? config;
      await updateConfig({
        cvPatterns: cur.cvPatterns,
        motivationPatterns: cur.motivationPatterns,
        generateOnSave: generateSelect.value as 'auto' | 'manual',
      });
      status.textContent = 'Saved.';
      window.setTimeout(() => (status.textContent = ''), 2000);
    } catch (err) {
      status.textContent = `Save failed: ${err instanceof Error ? err.message : String(err)}`;
    }
  };

  return el(
    'div',
    { class: 'stack stack-md' },
    sectionHeader(
      'Document generation',
      'When the AI should produce tailored documents for new jobs.',
    ),
    el(
      'div',
      { class: 'stack stack-xs' },
      field('When to generate'),
      generateSelect,
      hint(
        'Auto needs AI on and a non-empty profile. Otherwise the capture just lands as Saved.',
      ),
    ),
    el(
      'div',
      { class: 'actions-row' },
      el('button', { class: 'btn primary', onclick: save }, 'Save'),
      status,
    ),
  );
}

function renderBackupSection(root: FileSystemDirectoryHandle): HTMLElement {
  const status = el('div', { class: 'status-text' });

  const onExport = async (): Promise<void> => {
    status.textContent = 'Bundling folder…';
    try {
      const { blob, filename, fileCount } = await exportRootAsZip(root);
      downloadBlob(blob, filename);
      status.textContent = `Exported ${fileCount} files as ${filename}.`;
    } catch (err) {
      status.textContent = `Export failed: ${err instanceof Error ? err.message : String(err)}`;
    }
  };

  const onImport = async (): Promise<void> => {
    if (
      !confirm(
        'Import will overwrite files at conflicting paths under your root folder. Continue?',
      )
    )
      return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip,application/zip';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      status.textContent = `Unpacking ${file.name}…`;
      try {
        const { written, skipped } = await importZipIntoRoot(root, file, { onConflict: 'overwrite' });
        status.textContent = `Imported ${written} files (${skipped} skipped).`;
        try { await loadManifest(root); } catch { /* ignore */ }
      } catch (err) {
        status.textContent = `Import failed: ${err instanceof Error ? err.message : String(err)}`;
      }
    };
    input.click();
  };

  return el(
    'div',
    { class: 'stack stack-md' },
    sectionHeader('Backup', 'Move your workspace between machines or share an anonymised sample.'),
    el(
      'div',
      { class: 'actions-row' },
      el('button', { class: 'btn primary', onclick: onExport }, 'Export everything as ZIP'),
      el('button', { class: 'btn', onclick: onImport }, 'Import from ZIP'),
      status,
    ),
    hint(
      'ZIP layout matches the on-disk layout: ',
      el('code', { class: 'code-tag' }, 'applications.json'),
      ', ',
      el('code', { class: 'code-tag' }, 'profile.json'),
      ', templates, and one folder per job.',
    ),
  );
}
